import { isBrowser } from "./is-browser";

declare global {
  interface Window {
    env: {
      NODE_ENV: string;
      SUPABASE_URL: string;
      SUPABASE_ANON_PUBLIC: string;
      PUBLIC_MAPBOX_API_KEY: string;
      PUBLIC_GOOGLE_MAPS_API_KEY: string;
      GA_TRACKING_ID: string;
      POSTHOG_API_KEY: string;
      PRODUCTFRUITS_WORKSPACE_CODE: string;
      NOVU_CLIENT_APP_ID: string;
      SENTRY_DSN: string;
      VERCEL_ENV: string;
      REFLAG_PUBLISHABLE_KEY: string;
      VERIFY_INTRODUCTORY_ENDS_AT: string;
    };
  }
}

declare global {
  namespace NodeJS {
    interface ProcessEnv {
      SUPABASE_URL: string;
      SUPABASE_SERVICE_ROLE: string;
      SUPABASE_ANON_PUBLIC: string;
      SESSION_SECRET: string;
      PUBLIC_MAPBOX_API_KEY: string;
      PUBLIC_GOOGLE_MAPS_API_KEY: string;
      REFLAG_SECRET_KEY: string;
      GA_TRACKING_ID: string;
      POSTHOG_API_KEY: string;
      DISCORD_TOKEN: string;
      SLACK_BOT_TOKEN: string;
      NOVU_SECRET_KEY: string;
      UPSTASH_REDIS_REST_URL: string;
      UPSTASH_REDIS_REST_TOKEN: string;
      REINZ_CLIENT_ID: string;
      REINZ_CLIENT_SECRET: string;
      REINZ_REDIRECT_URI: string;
      VAULTRE_CLIENT_ID: string;
      VAULTRE_API_BASE_URL: string;
      REALGROWTH_CLIENT_ID?: string;
      REALGROWTH_CLIENT_SECRET?: string;
      REALGROWTH_API_BASE_URL?: string;
      REALGROWTH_MOCK?: string;
      REALGROWTH_CAPTURE_RAW?: string;
      VERIFY_FOUNDING_ENDS_AT?: string;
      VERIFY_INTRODUCTORY_ENDS_AT?: string;
      VERCEL_ENV?: string; // Used for IS_PRODUCTION calculation
    }
  }
}

type EnvOptions = {
  isSecret?: boolean;
  isRequired?: boolean;
};
function getEnv(
  name: string,
  { isRequired, isSecret }: EnvOptions = { isSecret: true, isRequired: true }
) {
  if (isBrowser && isSecret) return "";

  const source = (isBrowser ? window.env : process.env) ?? {};

  const value = source[name as keyof typeof source];

  if (!value && isRequired) {
    throw new Error(`${name} is not set`);
  }

  return value || "";
}

/**
 * Server env
 */
export const SUPABASE_SERVICE_ROLE = getEnv("SUPABASE_SERVICE_ROLE");
export const SESSION_SECRET = getEnv("SESSION_SECRET");
export const STRIPE_SECRET_KEY = getEnv("STRIPE_SECRET_KEY");
export const STRIPE_WEBHOOK_ENDPOINT_SECRET = getEnv(
  "STRIPE_WEBHOOK_ENDPOINT_SECRET"
);
export const HYGRAPH_CONTENT_API = getEnv("HYGRAPH_CONTENT_API");
export const DISCORD_TOKEN = getEnv("DISCORD_TOKEN", {
  isSecret: true,
  isRequired: false,
});
export const SLACK_BOT_TOKEN = getEnv("SLACK_BOT_TOKEN", {
  isSecret: true,
  isRequired: false,
});
export const NOVU_SECRET_KEY = getEnv("NOVU_SECRET_KEY");
export const UPSTASH_REDIS_REST_URL = getEnv("UPSTASH_REDIS_REST_URL");
export const UPSTASH_REDIS_REST_TOKEN = getEnv("UPSTASH_REDIS_REST_TOKEN");
export const REINZ_CLIENT_ID = getEnv("REINZ_CLIENT_ID");
export const REINZ_CLIENT_SECRET = getEnv("REINZ_CLIENT_SECRET");
export const REINZ_REDIRECT_URI = getEnv("REINZ_REDIRECT_URI");
export const REAL_GROWTH_LISTING_HUB_SECRET = getEnv(
  "REAL_GROWTH_LISTING_HUB_SECRET",
  {
    isSecret: true,
    isRequired: false,
  }
);

// Konnect Verify — Real Growth Contact Enhance API (NZ council records).
// Optional: the feature is Reflag-gated and falls back to a deterministic
// mock when creds are absent or REALGROWTH_MOCK is set.
export const REALGROWTH_CLIENT_ID = getEnv("REALGROWTH_CLIENT_ID", {
  isSecret: false,
  isRequired: false,
});
export const REALGROWTH_CLIENT_SECRET = getEnv("REALGROWTH_CLIENT_SECRET", {
  isSecret: true,
  isRequired: false,
});
export const REALGROWTH_API_BASE_URL = getEnv("REALGROWTH_API_BASE_URL", {
  isSecret: false,
  isRequired: false,
});
export const REALGROWTH_MOCK = getEnv("REALGROWTH_MOCK", {
  isSecret: false,
  isRequired: false,
});

// Persist each raw Real Growth response alongside the job's verdicts, so a
// mapping mistake can be re-judged against what the API actually said instead
// of re-running the check and paying for the same answers twice.
export const REALGROWTH_CAPTURE_RAW = getEnv("REALGROWTH_CAPTURE_RAW", {
  isSecret: false,
  isRequired: false,
});

// VaultRE Integration
export const VAULTRE_CLIENT_ID = getEnv("VAULTRE_CLIENT_ID", {
  isSecret: true,
  isRequired: false,
});
export const VAULTRE_API_BASE_URL = getEnv("VAULTRE_API_BASE_URL", {
  isSecret: false,
  isRequired: false,
});
export const VAULTRE_API_KEY = getEnv("VAULTRE_API_KEY", {
  isSecret: true,
  isRequired: false,
});
export const VAULTRE_ACCESS_TOKEN = getEnv("VAULTRE_ACCESS_TOKEN", {
  isSecret: true,
  isRequired: false,
});
/** Signs the JWT for VaultRE's integrator endpoints (see integrator.server.ts). */
export const VAULTRE_INTEGRATOR_SECRET = getEnv("VAULTRE_INTEGRATOR_SECRET", {
  isSecret: true,
  isRequired: false,
});
export const VAULTRE_REDIRECT_URI = getEnv("VAULTRE_REDIRECT_URI", {
  isSecret: false,
  isRequired: false,
});

/**
 * Shared envs
 */
export const NODE_ENV = getEnv("NODE_ENV", {
  isSecret: false,
  isRequired: false,
});
export const SUPABASE_URL = getEnv("SUPABASE_URL", { isSecret: false });
export const SUPABASE_ANON_PUBLIC = getEnv("SUPABASE_ANON_PUBLIC", {
  isSecret: false,
});
export const PUBLIC_MAPBOX_API_KEY = getEnv("PUBLIC_MAPBOX_API_KEY", {
  isSecret: false,
});
export const PUBLIC_GOOGLE_MAPS_API_KEY = getEnv("PUBLIC_GOOGLE_MAPS_API_KEY", {
  isSecret: false,
});

export const REFLAG_SECRET_KEY = getEnv("REFLAG_SECRET_KEY", {
  isSecret: true,
  isRequired: false,
});

export const REFLAG_PUBLISHABLE_KEY = getEnv("REFLAG_PUBLISHABLE_KEY", {
  isSecret: false,
  isRequired: false,
});

export const GA_TRACKING_ID = getEnv("GA_TRACKING_ID", {
  isSecret: false,
  isRequired: false,
});

export const POSTHOG_API_KEY = getEnv("POSTHOG_API_KEY", {
  isSecret: false,
  isRequired: false,
});

export const PRODUCTFRUITS_WORKSPACE_CODE = getEnv(
  "PRODUCTFRUITS_WORKSPACE_CODE",
  {
    isSecret: false,
    isRequired: false,
  }
);
export const NOVU_CLIENT_APP_ID = getEnv("NOVU_CLIENT_APP_ID", {
  isSecret: false,
  isRequired: false,
});

export const SENTRY_DSN = getEnv("SENTRY_DSN", {
  isSecret: false,
  isRequired: false,
});

// Activate — ISO date at which introductory pricing steps up to the list
// ladder. Shared (browser-exposed) so the isomorphic pricing module can
// compute the same quote on server and client. Not a secret. Empty →
// introductory pricing indefinitely (the list price is the strike-through;
// no end date is shown). `VERIFY_FOUNDING_ENDS_AT` is the previous name.
const VERIFY_INTRODUCTORY_ENDS_AT_VALUE =
  getEnv("VERIFY_INTRODUCTORY_ENDS_AT", {
    isSecret: false,
    isRequired: false,
  }) ||
  getEnv("VERIFY_FOUNDING_ENDS_AT", {
    isSecret: false,
    isRequired: false,
  });
export const VERIFY_INTRODUCTORY_ENDS_AT = VERIFY_INTRODUCTORY_ENDS_AT_VALUE;

export const VERCEL_ENV = getEnv("VERCEL_ENV", {
  isSecret: false,
  isRequired: false,
});

//  MORE SERVER ENV
// Check if we are in production and if we are in a Github Action
export const IS_PRODUCTION =
  NODE_ENV === "production" && getEnv("VERCEL_ENV") === "production";

export const getBrowserEnv = (): typeof window.env => ({
  NODE_ENV,
  SUPABASE_URL,
  SUPABASE_ANON_PUBLIC,
  PUBLIC_MAPBOX_API_KEY,
  PUBLIC_GOOGLE_MAPS_API_KEY,
  GA_TRACKING_ID,
  POSTHOG_API_KEY,
  PRODUCTFRUITS_WORKSPACE_CODE,
  NOVU_CLIENT_APP_ID,
  SENTRY_DSN,
  VERCEL_ENV,
  REFLAG_PUBLISHABLE_KEY,
  VERIFY_INTRODUCTORY_ENDS_AT: VERIFY_INTRODUCTORY_ENDS_AT_VALUE,
});
